/**
 * Domain: timing jitter.
 *
 * Add optional random delay to a scheduled timestamp.
 * - maxJitterMs = 0 => precise scheduling (no change)
 * - maxJitterMs > 0 => add random delay in [0, maxJitterMs]
 *
 * No I/O. Randomness is injected via rng for testability.
 */

/**
 * Random number generator function type (returns value in [0, 1)).
 */
export type RngFn = () => number;

/**
 * Compute a jitter delay in milliseconds.
 *
 * @param maxJitterMs - Maximum jitter delay (0 or negative means no jitter)
 * @param rng - Random number generator returning [0, 1)
 * @returns Integer delay in ms
 */
export function computeJitterDelayMs(maxJitterMs: number, rng: RngFn): number {
  const max = Number(maxJitterMs);
  if (!Number.isFinite(max) || max <= 0) return 0;
  const r = typeof rng === 'function' ? rng() : 0;
  const clampedR = Math.min(0.999999999, Math.max(0, Number(r)));
  return Math.floor(clampedR * (Math.floor(max) + 1));
}

/**
 * Apply random start jitter to a Date.
 *
 * @param when - Base timestamp
 * @param maxJitterMs - Maximum jitter delay (0 or negative means no jitter)
 * @param rng - Random number generator (defaults to Math.random)
 * @returns New Date with jitter applied
 */
export function applyStartJitter(
  when: Date,
  maxJitterMs: number,
  rng: RngFn = Math.random
): Date {
  if (!(when instanceof Date)) throw new Error('when must be a Date');
  const delay = computeJitterDelayMs(maxJitterMs, rng);
  return new Date(when.getTime() + delay);
}

